import { useMemo } from "react";
import { Variable, VariableType } from "../../../models/interactions/Variable";
import {
  getBuiltInFriendlyLabel,
  isBuiltInVariable,
} from "../../../models/interactions/BuiltInSequenceVariables";
import NodeStringDropdown from "../components/NodeStringDropdown";

interface VariableSelectorProps {
  variables: Variable[];
  selectedVariableId: string;
  label: string;
  onSelect: (variableId: string) => void;
  filterByType?: VariableType; // Optional type filter for type-safe operations
  includeBuiltIns?: boolean;
}

const isCompatibleType = (variableType: VariableType, targetType: VariableType) => {
  if (targetType === VariableType.String || targetType === VariableType.BuiltIn) {
    return (
      variableType === VariableType.String ||
      variableType === VariableType.BuiltIn
    );
  }
  return variableType === targetType;
};

export default function VariableSelector({
  variables,
  selectedVariableId,
  label,
  onSelect,
  filterByType,
  includeBuiltIns = true,
}: VariableSelectorProps) {
  const filteredVariables = useMemo(() => {
    return variables.filter((variable) => {
      if (!includeBuiltIns && isBuiltInVariable(variable)) {
        return false;
      }

      if (filterByType) {
        return isCompatibleType(variable.type, filterByType);
      }

      return true;
    });
  }, [variables, filterByType, includeBuiltIns]);

  const variableItems = useMemo<Record<string, string>>(() => {
    return filteredVariables.reduce((items, variable) => {
      const label = isBuiltInVariable(variable)
        ? `${getBuiltInFriendlyLabel(variable.name)} (${variable.name})`
        : variable.name;
      items[label] = variable.id;
      return items;
    }, {} as Record<string, string>);
  }, [filteredVariables]);

  const selectedVariable = filteredVariables.find((v) => v.id === selectedVariableId);
  const selectedText = selectedVariable
    ? isBuiltInVariable(selectedVariable)
      ? `${getBuiltInFriendlyLabel(selectedVariable.name)} (${selectedVariable.name})`
      : selectedVariable.name
    : "None";

  const handleSelect = (item: string) => {
    const variableId = variableItems[item];
    if (variableId) {
      onSelect(variableId);
    }
  };

  return (
    <NodeStringDropdown
      selectedItem={selectedText}
      items={Object.keys(variableItems)}
      label={label}
      onClick={handleSelect}
    />
  );
}
